import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import config from "../../config/config.js";
import { toast } from "react-toastify";
import Drawer from "react-modern-drawer";
import "react-modern-drawer/dist/index.css";

import { useAuth } from '../login/AuthContext';
import {
  Box,
  Button,
  TextField,
  Checkbox,
  FormControl,
  FormLabel,
  FormGroup,
  FormControlLabel,
  CircularProgress,
  Paper,
  Typography,
  Grid,
  IconButton,
  Divider,
  Chip,
  Card,
  CardContent,
  Fade,
  Badge,
  Switch,
} from "@mui/material";
import {
  AddCircleOutline,
  Close,
  Edit,
  Apps,
  People,
  CheckCircle,
  Extension,
  Save,
} from "@mui/icons-material";
import "./Packages.css";

import { useParams } from "react-router-dom";
import Loading from "../../components/Loading/Loading.jsx";
import { isPaketEtkin } from "../../utils/paketEtkin.js";

export const Packages = () => {
  const { id } = useParams();
  const [paketAdi, setPaketAdi] = useState("");
  const [maxKullaniciSayisi, setMaxKullaniciSayisi] = useState(1);
  const [sira, setSira] = useState("");
  const [moduller, setModuller] = useState([]);
  const [selectedModules, setSelectedModules] = useState([]);
  const [selectedAddModules, setSelectedAddModules] = useState([]);
  const [packagesDetails, setPackagesDetails] = useState([]);
  const [isDrawerOpen, setDrawerOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isDrawerLoading, setIsDrawerLoading] = useState(false);
  const { userData, token } = useAuth();
  const [editMode, setEditMode] = useState(false);
  const [selectedPaketId, setSelectedPaketId] = useState(null);
  const [etkinSavingId, setEtkinSavingId] = useState(null);

  const handleTogglePaketEtkin = async (paketId, yeniEtkin) => {
    try {
      setEtkinSavingId(paketId);
      const res = await axios.patch(
        `${config.baseURL}/paketler/${paketId}/etkin`,
        { Etkin: yeniEtkin },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data?.success) {
        setPackagesDetails((prev) =>
          prev.map((p) =>
            p.PaketId === paketId ? { ...p, Etkin: yeniEtkin } : p
          )
        );
        toast.success(res.data.message || (yeniEtkin ? "Paket aktif edildi" : "Paket pasif edildi"));
      }
    } catch (error) {
      console.error("Paket etkin güncelleme:", error);
      toast.error(error.response?.data?.message || "Paket durumu güncellenemedi");
    } finally {
      setEtkinSavingId(null);
    }
  };
  // Fetch Models
  const fetchModels = useCallback(async () => {
    try {
      setIsLoading(true);
      const res = await axios.get(`${config.baseURL}/program`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (res.status === 200) {
        setModuller(res.data);
      }
    } catch (error) {
      console.error("Error fetching programs:", error);
      toast.error("Modüller alınamadı: " + error.message);
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  // Fetch Packages Details
  const fetchPackagesDetail = useCallback(async () => {
    try {
      setIsLoading(true);
      const res = await axios.get(`${config.baseURL}/paketler/paket-detay-list`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (res.status === 200) {
        setPackagesDetails(res.data);
      }
    } catch (error) {
      console.error("Error fetching packages:", error);
      toast.error("Paketler detayları alınamadı: " + error.message);
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  // Fetch Paket Detayları için yeni endpoint
  const fetchPaketDetail = useCallback(async (paketId) => {
    try {
      setIsDrawerLoading(true);
      const res = await axios.get(`${config.baseURL}/paketler/paket-detay/${paketId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.status === 200) {
        const paketData = res.data;

        // Paket adını ve seçili modülleri ayarla
        setPaketAdi(paketData.PaketAdi || "");
        setMaxKullaniciSayisi(paketData.MaxKullaniciSayisi || "");
        setSira(paketData.Sira != null ? String(paketData.Sira) : "");

        // Ana modüller ve ek modülleri ayarla
        const mainModules = paketData.ModulDetaylar?.filter(m => m.EkModul !== true) || [];
        const additionalModules = paketData.EkModulDetaylar || [];

        // Uygun modül nesnelerini bul
        const selectedMainModuleObjects = mainModules.map(md =>
          moduller.find(m => m.Id === md.ModulId) || { Id: md.ModulId }
        ).filter(m => m);

        const selectedAddModuleObjects = additionalModules.map(ad =>
          moduller.find(m => m.Id === ad.ModuleId) || { Id: ad.ModuleId }
        ).filter(m => m);

        setSelectedModules(selectedMainModuleObjects);
        setSelectedAddModules(selectedAddModuleObjects);
      }
    } catch (error) {
      console.error("Error fetching paket detail:", error);
      toast.error("Paket detayları alınamadı: " + error.message);
    } finally {
      setIsDrawerLoading(false);
    }
  }, [token, moduller]);

  useEffect(() => {
    fetchPackagesDetail();
    fetchModels();
  }, [fetchPackagesDetail, fetchModels]);

  // Handle Selected Modules Change
  const handleModuleChange = (modul, isChecked) => {
    const updatedSelectedModules = isChecked
      ? [...selectedModules, modul]
      : selectedModules.filter((mod) => mod.Id !== modul.Id);

    const updatedSelectedAddModules = updatedSelectedModules.some((mod) => mod.Id === modul.Id)
      ? selectedAddModules.filter((mod) => mod.Id !== modul.Id)
      : selectedAddModules;

    setSelectedModules(updatedSelectedModules);
    setSelectedAddModules(updatedSelectedAddModules);
  };

  const handleAddModuleChange = (modul, isChecked) => {
    if (!selectedModules.some((mod) => mod.Id === modul.Id)) {
      const updatedSelectedAddModules = isChecked
        ? [...selectedAddModules, modul]
        : selectedAddModules.filter((mod) => mod.Id !== modul.Id);

      setSelectedAddModules(updatedSelectedAddModules);
    }
  };

  // Paket Düzenleme Fonksiyonu
  const handleEditPaket = (paketId) => {
    setSelectedPaketId(paketId);
    setEditMode(true);
    fetchPaketDetail(paketId);
    setDrawerOpen(true);
  };

  // Handle Form Submit
  const handleSubmit = async () => {
    if (!paketAdi || (selectedModules.length === 0 && selectedAddModules.length === 0)) {
      toast.error("Lütfen paket adını girin ve en az bir modül seçin.");
      return;
    }

    const siraNo = parseInt(sira, 10);
    if (sira === "" || Number.isNaN(siraNo) || siraNo < 1) {
      toast.error("Lütfen geçerli bir sıra numarası girin (1 veya daha büyük).");
      return;
    }

    const sortedModules = selectedModules.sort((a, b) => a.SiraNo - b.SiraNo);
    const sortedAddModules = selectedAddModules.sort((a, b) => a.SiraNo - b.SiraNo);

    const requestData = {
      paketAdi,
      maxKullaniciSayisi,
      sira: siraNo,
      selectedModules: sortedModules.map((modul) => modul.Id),
      selectedAddModules: sortedAddModules.map((modul) => modul.Id),
    };

    try {
      setIsLoading(true);

      let response;

      if (editMode && selectedPaketId) {
        // Düzenleme modu - PUT isteği
        requestData.paketId = selectedPaketId;
        response = await axios.put(
          `${config.baseURL}/paketler/update`,
          requestData,
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        );
        toast.success("Paket başarıyla güncellendi.");
      } else {
        // Ekleme modu - POST isteği
        response = await axios.post(
          `${config.baseURL}/paketler/create`,
          requestData,
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        );
        toast.success(response.data.message || "Paket başarıyla kaydedildi.");
      }

      // Reset form state
      resetForm();
      fetchPackagesDetail();
    } catch (error) {
      console.error(editMode ? "Paket güncellenirken hata oluştu:" : "Paket oluşturulurken hata oluştu:", error);
      toast.error(error.response?.data?.message || (editMode ? "Paket güncellenemedi." : "Paket kaydedilemedi."));
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setPaketAdi("");
    setMaxKullaniciSayisi(1);
    setSira("");
    setSelectedModules([]);
    setSelectedAddModules([]);
    setEditMode(false);
    setSelectedPaketId(null);
    setDrawerOpen(false);
  };

  const getNextSiraNo = () => {
    if (!packagesDetails.length) return 1;
    const maxSira = Math.max(
      0,
      ...packagesDetails.map((p) =>
        p.Sira != null && !Number.isNaN(Number(p.Sira)) ? Number(p.Sira) : 0
      )
    );
    return maxSira + 1;
  };

  const handleOpenNewPaket = () => {
    setPaketAdi("");
    setMaxKullaniciSayisi(1);
    setSelectedModules([]);
    setSelectedAddModules([]);
    setEditMode(false);
    setSelectedPaketId(null);
    setSira(String(getNextSiraNo()));
    setDrawerOpen(true);
  };

  return (
    <>
    {isLoading ? (
      <Loading />
    ) : (
    <Box
      className="packages-container"
      p={{ xs: 2, sm: 3, md: 4, lg: 5 }}
    >


      {/* Header Section */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 600,
              color: '#1F2937',
              mb: 1
            }}
          >
            Paketler
          </Typography>
          <Typography variant="body2" sx={{ color: '#6B7280' }}>
            Mevcut paketleri görüntüleyin ve yönetin
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddCircleOutline />}
          onClick={handleOpenNewPaket}
          disabled={isLoading}
          sx={{
            borderRadius: 2,
            px: 3,
            py: 1.5,
            textTransform: 'none',
            fontSize: '1rem',
            fontWeight: 500,
            backgroundColor: '#1e293b',
            boxShadow: '0 2px 8px rgba(28, 53, 94, 0.2)',
            '&:hover': {
              backgroundColor: '#1e293b',
              boxShadow: '0 4px 12px rgba(28, 53, 94, 0.3)',
            },
            transition: 'all 0.2s ease'
          }}
        >
          Yeni Paket Ekle
        </Button>
      </Box>


      {/* Package List with Grid */}
      <Grid container spacing={3} className="packages-grid">
        {[...packagesDetails]
          .sort((a, b) => {
            const sa = a.Sira != null ? Number(a.Sira) : 2147483647;
            const sb = b.Sira != null ? Number(b.Sira) : 2147483647;
            return sa - sb || (a.PaketId ?? 0) - (b.PaketId ?? 0);
          })
          .map((paketDetail, index) => {
          const aktif = isPaketEtkin(paketDetail);
          return (
          <Grid
            item
            xs={12}
            sm={12}
            md={6}
            lg={4}
            key={paketDetail.PaketId}
          >
            <Fade in timeout={300 + index * 100}>
              <Card
                className="package-card-modern"
                sx={{
                  height: "100%",
                  position: "relative",
                  borderRadius: 2,
                  overflow: 'hidden',
                  backgroundColor: '#FFFFFF',
                  border: aktif ? '1px solid #E5E7EB' : '1px solid #FECACA',
                  opacity: aktif ? 1 : 0.72,
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    transform: aktif ? 'translateY(-4px)' : 'none',
                    boxShadow: aktif ? '0 8px 24px rgba(28, 53, 94, 0.12)' : 'none',
                    borderColor: aktif ? '#1e293b' : '#FECACA',
                  }
                }}
              >
                {/* Header */}
                <Box
                  sx={{
                    backgroundColor: aktif ? '#1e293b' : '#6B7280',
                    p: 2.5,
                    color: 'white',
                    position: 'relative',
                    borderBottom: '1px solid #E5E7EB'
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      mb: 0.5,
                      fontSize: '1.1rem',
                      color: '#FFFFFF',
                      pr: 10,
                    }}
                  >
                    {paketDetail.PaketAdi}
                  </Typography>
                  {paketDetail.Sira != null && (
                    <Typography
                      variant="caption"
                      sx={{ color: 'rgba(255,255,255,0.85)', fontWeight: 500 }}
                    >
                      Sıra: {paketDetail.Sira}
                    </Typography>
                  )}

                  {/* Aktif / Pasif */}
                  <Box
                    sx={{
                      position: "absolute",
                      top: 8,
                      right: 44,
                      display: "flex",
                      alignItems: "center",
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <FormControlLabel
                      control={
                        <Switch
                          size="small"
                          checked={aktif}
                          disabled={etkinSavingId === paketDetail.PaketId}
                          onChange={(e) =>
                            handleTogglePaketEtkin(paketDetail.PaketId, e.target.checked)
                          }
                          sx={{
                            '& .MuiSwitch-switchBase.Mui-checked': { color: '#10B981' },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                              backgroundColor: '#10B981',
                            },
                          }}
                        />
                      }
                      label={
                        <Typography variant="caption" sx={{ color: '#fff', fontWeight: 600 }}>
                          {aktif ? "Aktif" : "Pasif"}
                        </Typography>
                      }
                      labelPlacement="start"
                      sx={{ m: 0, gap: 0.5 }}
                    />
                  </Box>
                  {!aktif && (
                    <Typography
                      variant="caption"
                      sx={{
                        display: 'block',
                        mt: 0.5,
                        color: 'rgba(255,255,255,0.85)',
                        fontStyle: 'italic',
                      }}
                    >
                      Pasif paket fiyat hesaplamada görünmez (ek modüller dahil).
                    </Typography>
                  )}

                  {/* Edit Button */}
                  <IconButton
                    sx={{
                      position: "absolute",
                      top: 8,
                      right: 8,
                      color: '#FFFFFF',
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.2)',
                      },
                      transition: 'all 0.2s ease'
                    }}
                    onClick={() => handleEditPaket(paketDetail.PaketId)}
                    size="small"
                  >
                    <Edit fontSize="small" />
                  </IconButton>
                </Box>

                <CardContent sx={{ p: 3, flexGrow: 1, display: 'flex', flexDirection: 'column', backgroundColor: '#FFFFFF' }}>
                  {/* Max Users Badge */}
                  <Box sx={{ mb: 2.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <People sx={{ color: '#1e293b', fontSize: 20 }} />
                    <Typography variant="body2" sx={{ color: '#6B7280', fontWeight: 500 }}>
                      Maksimum Kullanıcı:
                    </Typography>
                    <Chip
                      label={paketDetail.MaxKullaniciSayisi || "Belirtilmemiş"}
                      size="small"
                      sx={{
                        fontWeight: 500,
                        backgroundColor: '#E8EAF6',
                        color: '#1e293b',
                        border: '1px solid #E5E7EB'
                      }}
                    />
                  </Box>

                  <Divider sx={{ my: 2.5, borderColor: '#E5E7EB' }} />

                  {/* Aktif Modüller */}
                  <Box sx={{ mb: 2.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                      <Apps sx={{ color: '#098f69', fontSize: 20 }} />
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1F2937' }}>
                        Aktif Modüller
                      </Typography>
                    </Box>
                    <Typography
                      variant="body2"
                      sx={{
                        whiteSpace: "pre-line",
                        pl: 4.5,
                        lineHeight: 1.7,
                        minHeight: '3em',
                        color: '#6B7280'
                      }}
                    >
                      {paketDetail.Programlar || "Tanımsız"}
                    </Typography>
                  </Box>

                  <Divider sx={{ my: 2.5, borderColor: '#E5E7EB' }} />

                  {/* Ek Modüller */}
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                      <Extension sx={{ color: '#F59E0B', fontSize: 20 }} />
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1F2937' }}>
                        Ek Modüller
                      </Typography>
                    </Box>
                    <Typography
                      variant="body2"
                      sx={{
                        whiteSpace: "pre-line",
                        pl: 4.5,
                        lineHeight: 1.7,
                        fontStyle: paketDetail.EkModuller ? 'normal' : 'italic',
                        minHeight: '3em',
                        color: paketDetail.EkModuller ? '#6B7280' : '#9CA3AF'
                      }}
                    >
                      {paketDetail.EkModuller || "Ek modül bulunmamaktadır"}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Fade>
          </Grid>
        );
        })}
      </Grid>

      {/* Drawer - Add New Package */}
      <Drawer
        open={isDrawerOpen}
        onClose={resetForm}
        direction="right"
        className="add-package-drawer"
        duration={300}
        zIndex={600}
        style={{ width: "100%" }}
      >
        <Box
          sx={{
            width: "100%",
            height: "100%",
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: '#F9FAFB'
          }}
        >
          {/* Drawer Header */}
          <Box
            sx={{
              backgroundColor: '#1e293b',
              p: 3,
              color: 'white',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              borderBottom: '1px solid #E5E7EB',
              boxShadow: '0 2px 8px rgba(28, 53, 94, 0.1)'
            }}
          >
            <Typography variant="h5" sx={{ fontWeight: 600, color: '#FFFFFF' }}>
              {editMode ? "Paket Düzenle" : "Yeni Paket Oluştur"}
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={isLoading}
                startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <Save />}
                sx={{
                  py: 1,
                  px: 3,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  backgroundColor: '#FFFFFF',
                  color: '#1e293b',
                  boxShadow: 'none',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                  },
                  '&:disabled': {
                    backgroundColor: '#9CA3AF',
                    color: '#FFFFFF',
                  },
                  transition: 'all 0.2s ease'
                }}
              >
                {isLoading ? "Kaydediliyor..." : (editMode ? "Güncelle" : "Kaydet")}
              </Button>
              <IconButton
                onClick={resetForm}
                disabled={isLoading}
                sx={{
                  color: '#FFFFFF',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.15)',
                  },
                  transition: 'all 0.2s ease'
                }}
              >
                <Close />
              </IconButton>
            </Box>
          </Box>

          {/* Drawer Content */}
          <Box
            sx={{
              p: 4,
              flex: 1,
              overflowY: "auto",
              backgroundColor: '#F9FAFB',
              '&::-webkit-scrollbar': {
                width: '8px',
              },
              '&::-webkit-scrollbar-track': {
                background: '#F3F4F6',
                borderRadius: '10px',
              },
              '&::-webkit-scrollbar-thumb': {
                background: '#1e293b',
                borderRadius: '10px',
              },
              '&::-webkit-scrollbar-thumb:hover': {
                background: '#00A7B5',
              },
            }}
          >
            {/* Form Fields */}
            <Card
              sx={{
                mb: 3,
                borderRadius: 2,
                boxShadow: '0 2px 8px rgba(28, 53, 94, 0.08)',
                border: '1px solid #E5E7EB',
                backgroundColor: '#FFFFFF'
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#1F2937' }}>
                  Paket Bilgileri
                </Typography>
                <FormControl fullWidth margin="normal">
                  <TextField
                    label="Paket Adı"
                    variant="outlined"
                    value={paketAdi}
                    onChange={(e) => setPaketAdi(e.target.value)}
                    disabled={isLoading}
                    required
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        backgroundColor: '#FFFFFF',
                        '&:hover fieldset': {
                          borderColor: '#1e293b',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#1e293b',
                        },
                      },
                    }}
                  />
                </FormControl>

                <FormControl fullWidth margin="normal">
                  <TextField
                    label="Sıra Numarası"
                    type="number"
                    variant="outlined"
                    value={sira}
                    onChange={(e) => setSira(e.target.value)}
                    disabled={isLoading}
                    required
                    helperText="Fiyat hesaplamada paketlerin görünme sırası (küçük numara önce gelir)"
                    inputProps={{ min: 1, step: 1 }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        backgroundColor: '#FFFFFF',
                        '&:hover fieldset': {
                          borderColor: '#1e293b',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#1e293b',
                        },
                      },
                    }}
                  />
                </FormControl>

                <FormControl fullWidth margin="normal">
                  <TextField
                    label="Maksimum Kullanıcı Sayısı"
                    type="number"
                    variant="outlined"
                    value={maxKullaniciSayisi}
                    onChange={(e) => setMaxKullaniciSayisi(e.target.value)}
                    disabled={isLoading}
                    required
                    InputProps={{
                      startAdornment: <People sx={{ mr: 1, color: '#6B7280' }} />,
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        backgroundColor: '#FFFFFF',
                        '&:hover fieldset': {
                          borderColor: '#1e293b',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#1e293b',
                        },
                      },
                    }}
                  />
                </FormControl>
              </CardContent>
            </Card>

            {/* Modül ve Ek Modüller İçin Flex Yapısı */}
            <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, gap: 3, height: { md: "calc(100% - 300px)" } }}>
              {/* Modüller Kısmı */}
              <Card
                sx={{
                  flex: 1,
                  borderRadius: 2,
                  boxShadow: '0 2px 8px rgba(28, 53, 94, 0.08)',
                  border: '1px solid #E5E7EB',
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                  backgroundColor: '#FFFFFF'
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#E8EAF6',
                    p: 2,
                    borderBottom: '2px solid',
                    borderColor: '#1e293b',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5
                  }}
                >
                  <Apps sx={{ color: '#1e293b' }} />
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      fontSize: '1rem',
                      color: '#1F2937',
                      flex: 1
                    }}
                  >
                    Ana Modüller
                  </Typography>
                  <Chip
                    label={selectedModules.length}
                    size="small"
                    sx={{
                      backgroundColor: '#1e293b',
                      color: '#FFFFFF',
                      fontWeight: 500
                    }}
                  />
                </Box>
                <Box
                  sx={{
                    flex: 1,
                    overflowY: "auto",
                    p: 2,
                    backgroundColor: '#FFFFFF',
                    '&::-webkit-scrollbar': {
                      width: '6px',
                    },
                    '&::-webkit-scrollbar-track': {
                      background: '#F3F4F6',
                      borderRadius: '10px',
                    },
                    '&::-webkit-scrollbar-thumb': {
                      background: '#1e293b',
                      borderRadius: '10px',
                    },
                  }}
                >
                  <FormGroup>
                    {moduller.map((modul) => (
                      <FormControlLabel
                        key={modul.Id}
                        control={
                          <Checkbox
                            value={modul.Id}
                            checked={selectedModules.some((mod) => mod.Id === modul.Id)}
                            onChange={(e) => handleModuleChange(modul, e.target.checked)}
                            disabled={isLoading}
                            sx={{
                              color: '#1e293b',
                              '&.Mui-checked': {
                                color: '#1e293b',
                              },
                            }}
                          />
                        }
                        label={
                          <Typography
                            variant="body1"
                            sx={{
                              fontWeight: selectedModules.some((mod) => mod.Id === modul.Id) ? 600 : 400,
                              color: selectedModules.some((mod) => mod.Id === modul.Id) ? '#1e293b' : '#1F2937'
                            }}
                          >
                            {modul.ProgramAdi}
                          </Typography>
                        }
                        sx={{
                          mb: 1,
                          p: 1.5,
                          borderRadius: 2,
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            backgroundColor: '#F3F4F6',
                          }
                        }}
                      />
                    ))}
                  </FormGroup>
                </Box>
              </Card>

              {/* Ek Modüller Kısmı */}
              <Card
                sx={{
                  flex: 1,
                  borderRadius: 2,
                  boxShadow: '0 2px 8px rgba(28, 53, 94, 0.08)',
                  border: '1px solid #E5E7EB',
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                  backgroundColor: '#FFFFFF'
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#FEF3C7',
                    p: 2,
                    borderBottom: '2px solid',
                    borderColor: '#F59E0B',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5
                  }}
                >
                  <Extension sx={{ color: '#F59E0B' }} />
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      fontSize: '1rem',
                      color: '#1F2937',
                      flex: 1
                    }}
                  >
                    Ek Modüller
                  </Typography>
                  <Chip
                    label={selectedAddModules.length}
                    size="small"
                    sx={{
                      backgroundColor: '#F59E0B',
                      color: '#FFFFFF',
                      fontWeight: 500
                    }}
                  />
                </Box>
                <Box
                  sx={{
                    flex: 1,
                    overflowY: "auto",
                    p: 2,
                    backgroundColor: '#FFFFFF',
                    '&::-webkit-scrollbar': {
                      width: '6px',
                    },
                    '&::-webkit-scrollbar-track': {
                      background: '#F3F4F6',
                      borderRadius: '10px',
                    },
                    '&::-webkit-scrollbar-thumb': {
                      background: '#F59E0B',
                      borderRadius: '10px',
                    },
                  }}
                >
                  <FormGroup>
                    {moduller.map((modul) => {
                      const isDisabled = selectedModules.some((mod) => mod.Id === modul.Id) || isLoading;
                      const isSelected = selectedAddModules.some((mod) => mod.Id === modul.Id);
                      return (
                        <FormControlLabel
                          key={modul.Id}
                          control={
                            <Checkbox
                              value={modul.Id}
                              checked={isSelected}
                              onChange={(e) => handleAddModuleChange(modul, e.target.checked)}
                              disabled={isDisabled}
                              sx={{
                                color: '#F59E0B',
                                '&.Mui-checked': {
                                  color: '#F59E0B',
                                },
                                '&.Mui-disabled': {
                                  color: '#9CA3AF',
                                },
                              }}
                            />
                          }
                          label={
                            <Typography
                              variant="body1"
                              sx={{
                                fontWeight: isSelected ? 600 : 400,
                                color: isDisabled ? '#9CA3AF' : (isSelected ? '#F59E0B' : '#1F2937'),
                                textDecoration: isDisabled ? "line-through" : "none",
                                opacity: isDisabled ? 0.6 : 1
                              }}
                            >
                              {modul.ProgramAdi}
                            </Typography>
                          }
                          sx={{
                            mb: 1,
                            p: 1.5,
                            borderRadius: 2,
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              backgroundColor: isDisabled ? 'transparent' : '#F3F4F6',
                            }
                          }}
                        />
                      );
                    })}
                  </FormGroup>
                </Box>
              </Card>
            </Box>
          </Box>

        </Box>
      </Drawer>
    </Box>
    )}
  </>
  );
};